// src/pages/admin/PausedInterviewsPage.jsx
// Admin page for managing paused/disconnected interviews - standardized header

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPageView, trackReconnectSent } from '../../utils/analytics';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.invenire.app';

export default function PausedInterviewsPage() {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState({});
  const [successMessages, setSuccessMessages] = useState({});

  useEffect(() => {
    document.title = 'Invenire - Paused Interviews';
    trackPageView('/admin/paused', 'Paused Interviews');
  }, []);

  useEffect(() => {
    fetchPausedInterviews();
  }, []);

  const fetchPausedInterviews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/interviews/paused`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch paused interviews');
      }
      
      const data = await response.json();
      setInterviews(data.interviews || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendReconnectEmail = async (interviewId) => {
    setSendingEmail(prev => ({ ...prev, [interviewId]: true }));
    
    try {
      const response = await fetch(
        `${API_BASE}/interviews/${interviewId}/generate-reconnect?send_email=true`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to send reconnect email');
      }
      
      const data = await response.json();
      
      trackReconnectSent(interviewId, 'email');
      
      setSuccessMessages(prev => ({
        ...prev,
        [interviewId]: `Reconnect link sent to ${data.candidate_email}`
      }));
      
      setTimeout(() => {
        setSuccessMessages(prev => {
          const next = { ...prev };
          delete next[interviewId];
          return next;
        });
      }, 5000);
      
      await fetchPausedInterviews();
      
    } catch (err) {
      setError(`Failed to send email for interview ${interviewId}: ${err.message}`);
    } finally {
      setSendingEmail(prev => ({ ...prev, [interviewId]: false }));
    }
  };

  const copyReconnectLink = async (interviewId) => {
    try {
      const response = await fetch(
        `${API_BASE}/interviews/${interviewId}/generate-reconnect?send_email=false`,
        { method: 'POST' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate reconnect link');
      }
      
      const data = await response.json();
      await navigator.clipboard.writeText(data.reconnect_url);
      
      trackReconnectSent(interviewId, 'copy');
      
      setSuccessMessages(prev => ({
        ...prev,
        [interviewId]: 'Link copied to clipboard!'
      }));
      
      setTimeout(() => {
        setSuccessMessages(prev => {
          const next = { ...prev };
          delete next[interviewId];
          return next;
        });
      }, 3000);
      
    } catch (err) {
      setError(`Failed to copy link: ${err.message}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getPauseReasonLabel = (reason) => {
    switch (reason) {
      case 'disconnected':
        return { label: 'Disconnected', color: '#ef4444' };
      case 'user_paused':
        return { label: 'User Paused', color: '#eab308' };
      default:
        return { label: reason || 'Unknown', color: '#6b7280' };
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading paused interviews...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <img 
            src="/invenire-logo-transparent.png" 
            alt="Invenire" 
            style={styles.logoImage}
          />
          <span style={styles.subtitle}>Paused Interviews</span>
        </div>
        <div style={styles.headerActions}>
          <button 
            onClick={fetchPausedInterviews}
            style={styles.refreshButton}
          >
            ↻ Refresh
          </button>
          <button onClick={() => navigate('/admin')} style={styles.backButton}>
            ← Admin Home
          </button>
        </div>
      </div>

      <div style={styles.content}>
        <p style={styles.pageDescription}>
          Manage interviews that were interrupted or paused by candidates. Send reconnect links to allow candidates to resume.
        </p>

        {error && (
          <div style={styles.errorBanner}>
            {error}
            <button 
              onClick={() => setError(null)}
              style={styles.dismissButton}
            >
              ✕
            </button>
          </div>
        )}

        {interviews.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>✓</div>
            <h2>No Paused Interviews</h2>
            <p>All interviews are either in progress or completed.</p>
          </div>
        ) : (
          <div style={styles.list}>
            {interviews.map(interview => {
              const pauseInfo = getPauseReasonLabel(interview.pause_reason);
              
              return (
                <div key={interview.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div>
                      <h3 style={styles.candidateName}>
                        {interview.candidate_name || interview.candidate_email || 'Unknown Candidate'}
                      </h3>
                      <p style={styles.jobTitle}>{interview.job_title}</p>
                    </div>
                    <span style={{
                      ...styles.pauseBadge,
                      backgroundColor: `${pauseInfo.color}20`,
                      color: pauseInfo.color,
                      borderColor: pauseInfo.color
                    }}>
                      {pauseInfo.label}
                    </span>
                  </div>

                  <div style={styles.cardDetails}>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Paused At:</span>
                      <span>{formatDate(interview.paused_at)}</span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Progress:</span>
                      <span>
                        {interview.questions_asked} of {interview.total_questions} questions
                      </span>
                    </div>
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>Sessions:</span>
                      <span>{interview.session_count || 1}</span>
                    </div>
                    {interview.has_reconnect_token && (
                      <div style={styles.detailRow}>
                        <span style={styles.detailLabel}>Reconnect Link:</span>
                        <span style={{ color: '#22c55e' }}>✓ Generated</span>
                      </div>
                    )}
                  </div>

                  {successMessages[interview.id] && (
                    <div style={styles.successMessage}>
                      {successMessages[interview.id]}
                    </div>
                  )}

                  <div style={styles.cardActions}>
                    <button
                      onClick={() => sendReconnectEmail(interview.id)}
                      disabled={sendingEmail[interview.id]}
                      style={styles.primaryButton}
                    >
                      {sendingEmail[interview.id] ? 'Sending...' : '📧 Send Reconnect Email'}
                    </button>
                    <button
                      onClick={() => copyReconnectLink(interview.id)}
                      style={styles.secondaryButton}
                    >
                      📋 Copy Link
                    </button>
                    <button
                      onClick={() => navigate(`/admin/interviews/${interview.id}/report`)}
                      style={styles.linkButton}
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #11181C 0%, #1a2632 50%, #0a0f14 100%)',
    color: '#fff',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 40px',
    borderBottom: '1px solid rgba(255,255,255,0.1)'
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  },
  logoImage: {
    height: 48,
    width: 'auto',
    objectFit: 'contain'
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af'
  },
  headerActions: {
    display: 'flex',
    gap: 12
  },
  refreshButton: {
    padding: '8px 16px',
    borderRadius: 6,
    border: '1px solid rgba(148, 163, 184, 0.3)',
    background: 'transparent',
    color: '#e5e7eb',
    cursor: 'pointer',
    fontSize: 14
  },
  backButton: {
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    cursor: 'pointer'
  },
  content: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '32px 24px'
  },
  pageDescription: {
    color: '#9ca3af',
    marginBottom: 24
  },
  loading: {
    textAlign: 'center',
    padding: 60,
    color: '#9ca3af'
  },
  errorBanner: {
    padding: '12px 16px',
    borderRadius: 8,
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#f87171',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24
  },
  dismissButton: {
    background: 'transparent',
    border: 'none',
    color: '#f87171',
    cursor: 'pointer',
    fontSize: 16
  },
  emptyState: {
    maxWidth: 400,
    margin: '80px auto',
    textAlign: 'center',
    padding: 40,
    background: 'rgba(15, 23, 42, 0.5)',
    borderRadius: 16,
    border: '1px solid rgba(30, 64, 175, 0.3)'
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: 16,
    color: '#22c55e'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  card: {
    background: 'rgba(15, 23, 42, 0.6)',
    borderRadius: 12,
    border: '1px solid rgba(30, 64, 175, 0.4)',
    padding: 20,
    transition: 'border-color 0.2s'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16
  },
  candidateName: {
    fontSize: 18,
    fontWeight: 600,
    margin: '0 0 4px 0',
    color: '#fff'
  },
  jobTitle: {
    fontSize: 14,
    color: '#9ca3af',
    margin: 0
  },
  pauseBadge: {
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 500,
    border: '1px solid'
  },
  cardDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px 24px',
    marginBottom: 16,
    fontSize: 14
  },
  detailRow: {
    display: 'flex',
    gap: 8
  },
  detailLabel: {
    color: '#6b7280'
  },
  successMessage: {
    padding: '8px 12px',
    borderRadius: 6,
    background: 'rgba(34, 197, 94, 0.1)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    color: '#22c55e',
    fontSize: 14,
    marginBottom: 12
  },
  cardActions: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap'
  },
  primaryButton: {
    padding: '8px 16px',
    borderRadius: 8,
    border: 'none',
    background: '#3b82f6',
    color: '#fff',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s'
  },
  secondaryButton: {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid rgba(148, 163, 184, 0.4)',
    background: 'transparent',
    color: '#e5e7eb',
    fontSize: 14,
    cursor: 'pointer'
  },
  linkButton: {
    padding: '8px 16px',
    background: 'none',
    border: 'none',
    color: '#60a5fa',
    fontSize: 14,
    cursor: 'pointer'
  }
};
